import React, { useState, useEffect } from 'react';
import { useCCPOPRPSievesContext, CCP_SCREENS } from '../context/CCPOPRPSievesContext';
import { ccpOprpSievesService } from '../services/ccpOprpSievesService';
import {  SITES } from '../constants/ccpOprpSievesData';
import { useMsal } from "@azure/msal-react";
import { SP_MSAL_SCOPE } from '../../../../constants/sharepoint';
import moment from 'moment';

const ChecklistInformation: React.FC = () => {
    const { state, dispatch } = useCCPOPRPSievesContext();
    const { instance, accounts } = useMsal();

    const [parameterVerification, setParameterVerification] = useState<"CCP & OPRP" | "Sieves and Magnets">(
        state.parameterVerification || "CCP & OPRP"
    );
    const [site, setSite] = useState("Rajpura");
    const [line, setLine] = useState("Line-1");
    const [productVariety, setProductVariety] = useState("");
    const [frequency, setFrequency] = useState<"4hrs" | "8hrs">(state.selectedFrequency || "4hrs");
    
    // Shift Executive (logged-in user name)
    const shiftExecutiveName = accounts && accounts[0]?.name ? accounts[0].name : (accounts && accounts[0]?.username ? accounts[0].username : "Shift Executive");
    
    const [qaExecutive, setQaExecutive] = useState("");
    const [productionIncharge, setProductionIncharge] = useState("");
    const [rawStartDate] = useState(new Date().toISOString());
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [rawConfigs, setRawConfigs] = useState<any[]>([]);
    const [qaOptions, setQaOptions] = useState<{ label: string; value: string }[]>([]);
    const [prodOptions, setProdOptions] = useState<{ label: string; value: string }[]>([]);

    const productOptions = ccpOprpSievesService.getProducts(rawConfigs, line);

    const lineOptions = parameterVerification === "Sieves and Magnets"
        ? ["Line 5,6,7", "Line 1,2,3,4,8"]
        : ["Line-1", "Line-2", "Line-3", "Line-4", "Line-5", "Line-6", "Line-7", "Line-8", "FFS"];

    // Keep line selection valid when switching verification type
    useEffect(() => {
        if (parameterVerification === "Sieves and Magnets") {
            if (!["Line 5,6,7", "Line 1,2,3,4,8"].includes(line)) {
                setLine("Line 5,6,7");
            }
        } else {
            if (!["Line-1", "Line-2", "Line-3", "Line-4", "Line-5", "Line-6", "Line-7", "Line-8", "FFS"].includes(line)) {
                setLine("Line-1");
            }
        }
    }, [parameterVerification]);

    // Keep product selection valid when line or product options change
    useEffect(() => {
        if (productOptions.length > 0 && (!productVariety || !productOptions.some(p => p.Title === productVariety))) {
            setProductVariety(productOptions[0].Title);
        }
    }, [line, rawConfigs]);

    useEffect(() => {
        loadPersonnelConfig();
    }, [instance, accounts]);

    const loadPersonnelConfig = async () => {
        try {
            let spToken = "";
            if (navigator.onLine && accounts && accounts.length > 0) {
                try {
                    const tokenResponse = await instance.acquireTokenSilent({
                        scopes: [SP_MSAL_SCOPE],
                        account: accounts[0]
                    });
                    spToken = tokenResponse.accessToken;
                } catch (tokErr) {
                    console.warn("Silent token acquisition failed:", tokErr);
                }
            }

            const configs = await ccpOprpSievesService.getConfig(spToken);
            setRawConfigs(configs || []);
            filterPersonnel(configs || [], parameterVerification);
        } catch (e) {
            console.warn("Could not load personnel config:", e);
        }
    };

    useEffect(() => {
        if (rawConfigs.length > 0) {
            filterPersonnel(rawConfigs, parameterVerification);
        }
    }, [parameterVerification, rawConfigs]);

    const filterPersonnel = (configs: any[], typeVal: string) => {
        let matchingConfigs: any[] = [];
        if (typeVal === "Sieves and Magnets") {
            matchingConfigs = configs.filter(c => c.ConfigType === "Sieves_Magnets" || c.ConfigType === "Sieves and Magnets" || c.ChecklistType === "Sieves_Magnets");
        } else {
            matchingConfigs = configs.filter(c => c.ConfigType === "CCP_OPRP" || c.ConfigType === "CCP & OPRP" || c.ChecklistType === "CCP_OPRP");
        }

        if (matchingConfigs.length === 0) {
            matchingConfigs = configs;
        }

        const qasMap: Record<string, { email: string; title: string }> = {};
        const prodsMap: Record<string, { email: string; title: string }> = {};

        matchingConfigs.forEach(c => {
            const qaList = (c.AssignedQA && c.AssignedQA.results) || c.QAExecutives || (c.AssignedUser && c.AssignedUser.results) || (Array.isArray(c.AssignedQA) ? c.AssignedQA : []) || [];
            qaList.forEach((u: any) => {
                if (!u) return;
                const em = (u.EMail || u.email || "").trim();
                const title = (u.Title || u.title || em || "").trim();
                if (title || em) {
                    const k = (em || title).toLowerCase().trim();
                    qasMap[k] = { email: em || title, title: title || em };
                }
            });

            const prodList = (c.ProductionIncharge && c.ProductionIncharge.results) || c.ProductionIncharges || (c.ProductionExecutive && c.ProductionExecutive.results) || (Array.isArray(c.ProductionIncharge) ? c.ProductionIncharge : []) || [];
            prodList.forEach((u: any) => {
                if (!u) return;
                const em = (u.EMail || u.email || "").trim();
                const title = (u.Title || u.title || em || "").trim();
                if (title || em) {
                    const k = (em || title).toLowerCase().trim();
                    prodsMap[k] = { email: em || title, title: title || em };
                }
            });
        });

        const qaList = Object.values(qasMap).map(item => ({ label: item.title, value: item.email || item.title }));
        const prodList = Object.values(prodsMap).map(item => ({ label: item.title, value: item.email || item.title }));

        setQaOptions(qaList);
        setProdOptions(prodList);

        const myEmail = accounts && accounts[0]?.username ? accounts[0].username.toLowerCase().trim() : "";
        const myName = accounts && accounts[0]?.name ? accounts[0].name.toLowerCase().trim() : "";

        const matchQa = qaList.find(o => 
            (myEmail && o.value.toLowerCase() === myEmail) || 
            (myName && o.label.toLowerCase().includes(myName)) ||
            (myEmail && o.value.toLowerCase().includes(myEmail))
        );

        if (matchQa) {
            setQaExecutive(matchQa.value);
        } else if (qaList.length > 0) {
            setQaExecutive(qaList[0].value);
        }

        if (prodList.length > 0 && !productionIncharge) {
            setProductionIncharge(prodList[0].value);
        }
    };

    const handleStartTour = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!qaExecutive.trim()) {
            alert("Please select a QA Executive.");
            return;
        }

        if (!productionIncharge.trim()) {
            alert("Please select a Production Executive.");
            return;
        }

        setIsSubmitting(true);
        try {
            const shift = sessionStorage.getItem("shiftValue") || "Shift-1";
            const sessionPayload: any = {
                cr3ea_title: `${parameterVerification === "CCP & OPRP" ? "OPRP_CCP" : "Sieves"}_${moment().format("DD-MM-YYYY")}_${parameterVerification === "CCP & OPRP" ? line : site}`,
                cr3ea_plantid: site,
                cr3ea_shift: shift,
                cr3ea_tourstartdate: rawStartDate,
                cr3ea_assigned_qa: qaExecutive,
                cr3ea_shiftexecutiveproduction: productionIncharge,
                cr3ea_status: "In Progress",
                cr3ea_processstatus: "In Progress",
                cr3ea_ccp_oprp_sieves_parametertype: parameterVerification,
                cr3ea_ccp_oprp_sieves_frequency: parameterVerification === "Sieves and Magnets" ? frequency : undefined,
                cr3ea_ccp_oprp_sieves_productvariety: parameterVerification === "CCP & OPRP" ? productVariety : undefined,
                cr3ea_lineno: line,
                _isNew: true
            };

            const result = await ccpOprpSievesService.saveSession(sessionPayload);
            const tourId = result.id;

            dispatch({ type: 'SET_TOUR_ID', payload: tourId });
            dispatch({
                type: 'SET_SELECTIONS',
                payload: {
                    parameterVerification,
                    shift,
                    frequency: parameterVerification === "Sieves and Magnets" ? frequency : undefined
                }
            });
            dispatch({
                type: 'SET_SESSION_DATA',
                payload: {
                    site,
                    line,
                    shift,
                    qaExecutive,
                    productionIncharge,
                    productVariety: parameterVerification === "CCP & OPRP" ? productVariety : "",
                    parameterVerification,
                    frequency,
                    cycle: "Cycle-1",
                    tourStartDate: rawStartDate
                }
            });
            dispatch({ type: 'SET_CURRENT_CYCLE', payload: "Cycle-1" });

            if (parameterVerification === "CCP & OPRP") {
                dispatch({ type: 'SET_SCREEN', payload: CCP_SCREENS.CCP_OPRP_CHECKLIST });
            } else {
                dispatch({ type: 'SET_SCREEN', payload: CCP_SCREENS.SIEVES_MAGNETS_CHECKLIST });
            }
        } catch (err: any) {
            console.error("Error starting quality tour:", err);
            alert("Failed to start quality tour. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div id="setup-form-container" className="bs-card" style={{ maxWidth: 680, margin: '20px auto', padding: '25px' }}>
            <div className="bs-card-header" style={{ borderBottom: '2px solid #f1f5f9', paddingBottom: 15, marginBottom: 20 }}>
                <h3 className="bs-card-title" style={{ fontSize: 20, fontWeight: 'bold', color: '#0f172a' }}>
                    Initialize Quality Parameter Tour
                </h3>
            </div>
            <div className="bs-card-body" style={{ padding: 0 }}>
                <form onSubmit={handleStartTour}>
                    <div className="form-group">
                        <label className="form-label">Parameter Verification Type</label>
                        <select
                            id="setup-type"
                            className="form-select"
                            value={parameterVerification}
                            onChange={(e) => setParameterVerification(e.target.value as any)}
                        >
                            <option value="CCP & OPRP">CCP & OPRP</option>
                            <option value="Sieves and Magnets">Sieves and Magnets</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Manufacturing Site</label>
                        <select
                            id="setup-site"
                            className="form-select"
                            value={site}
                            onChange={(e) => setSite(e.target.value)}
                        >
                            {SITES.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group" id="setup-line-group">
                        <label className="form-label">
                            {parameterVerification === "Sieves and Magnets" ? "Line / Section" : "Line No"}
                        </label>
                        <select
                            id="setup-line"
                            className="form-select"
                            value={line}
                            onChange={(e) => setLine(e.target.value)}
                        >
                            {lineOptions.map(l => (
                                <option key={l} value={l}>{l.startsWith("Line-") ? l.replace("Line-", "Line ") : l}</option>
                            ))}
                        </select>
                    </div>

                    {parameterVerification === "CCP & OPRP" && (
                        <div className="form-group" id="setup-product-group">
                            <label className="form-label">Product Variety</label>
                            <select
                                id="setup-product"
                                className="form-select"
                                value={productVariety}
                                onChange={(e) => setProductVariety(e.target.value)}
                            >
                                {productOptions.map((p: any) => (
                                    <option key={p.Title} value={p.Title}>{p.Title}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {parameterVerification === "Sieves and Magnets" && (
                        <div className="form-group" id="setup-freq-group">
                            <label className="form-label">Sieve Check Frequency</label>
                            <select
                                id="setup-freq"
                                className="form-select"
                                value={frequency}
                                onChange={(e) => setFrequency(e.target.value as any)}
                            >
                                <option value="4hrs">4-Hour Intervals</option>
                                <option value="8hrs">Once a Shift (8-Hour Intervals)</option>
                            </select>
                        </div>
                    )}

                    <div className="form-group">
                        <label className="form-label">Shift Executive</label>
                        <input
                            type="text"
                            id="setup-shift-exec"
                            className="form-control"
                            style={{
                                height: 40,
                                borderRadius: 6,
                                border: '1px solid #cbd5e1',
                                backgroundColor: '#f1f5f9',
                                color: '#334155',
                                fontWeight: 600,
                                padding: '8px 12px'
                            }}
                            value={shiftExecutiveName}
                            disabled
                            readOnly
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Production Executive</label>
                        <select
                            id="setup-prod"
                            className="form-select"
                            value={productionIncharge}
                            onChange={(e) => setProductionIncharge(e.target.value)}
                        >
                            {prodOptions.length === 0 && <option value="">Loading...</option>}
                            {prodOptions.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group" style={{ marginBottom: 25 }}>
                        <label className="form-label">QA Executive</label>
                        <select
                            id="setup-qa"
                            className="form-select"
                            value={qaExecutive}
                            onChange={(e) => setQaExecutive(e.target.value)}
                        >
                            {qaOptions.length === 0 && <option value="">Loading...</option>}
                            {qaOptions.map(q => (
                                <option key={q.value} value={q.value}>{q.label}</option>
                            ))}
                        </select>
                    </div>

                    <button
                        type="submit"
                        id="start-tour-btn"
                        className="bs-btn bs-btn-primary w-100"
                        style={{ padding: 12, fontWeight: 600 }}
                        disabled={isSubmitting || !qaExecutive || !productionIncharge}
                    >
                        {isSubmitting ? "Starting..." : "Start Quality Tour"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ChecklistInformation;
